import { Alert } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import { useFormState } from 'react-hook-form';
import { AuthData } from '../../../context/auth';
import {
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import { RENEWAL_REVIEW_CHANGES_UNSAVED_CHANGES_MSG } from '../../../data/labelsAndMessages';
import { formatDateStringShort } from '../../../util/util';
import {
	StyledRow,
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../../common';
import FieldLabel from '../../fields/FieldLabel';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import ExposureChanges from '../ReviewChanges/ExposureChanges';
import PolicyChanges from '../ReviewChanges/PolicyChanges';
import {
	filterForPolicySnapshot,
	getAllCharacteristicsFieldGroupLocators,
	getAllCharacteristicsFieldValues,
	getHasPaymentPluginEnabled,
	getReferenceTimestampBasedOnState,
	renewalHasUnsavedChanges,
} from '../util_sharedEndorsementAndRenewal';

const useStyles = makeStyles((theme) => ({
	disabledInput: {
		'& .MuiInputBase-root.Mui-disabled': {
			color: 'rgba(0, 0, 0, 0.54)',
		},
	},
}));

interface Props {
	authData: AuthData;
	renewalData: RenewalData;
}

/**
 * The review changes page for an renewal.
 */
const ReviewChanges = (props: Props) => {
	const classes = useStyles();
	const { renewalData } = props;
	const { isDirty, dirtyFields } = useFormState();

	const { productConfig, renewalUpdateObj, policy, renewalResponse } =
		renewalData;

	if (!renewalUpdateObj) {
		return <LargeCenterSpinner />;
	}

	const { renewalEndTimestamp, renewalStartTimestamp, newPaymentScheduleName } =
		renewalUpdateObj;

	const { fields: fieldConfigurations, exposures: exposureConfigurations } =
		productConfig.policyConfiguration;

	// policy changes
	const changedFieldValues = renewalUpdateObj.fieldValues
		? { ...renewalUpdateObj.fieldValues }
		: {};
	const addedFieldGroups = renewalUpdateObj.addFieldGroups
		? [...renewalUpdateObj.addFieldGroups]
		: [];
	const removedFieldGroups = renewalUpdateObj.removeFieldGroups
		? [...renewalUpdateObj.removeFieldGroups]
		: [];
	const updatedFieldGroups = renewalUpdateObj.updateFieldGroups
		? [...renewalUpdateObj.updateFieldGroups]
		: [];

	// exposure changes
	const addedExposures = renewalUpdateObj.addExposures
		? [...renewalUpdateObj.addExposures]
		: [];
	const updatedExposures = renewalUpdateObj.updateExposures
		? [...renewalUpdateObj.updateExposures]
		: [];
	const endedExposures = renewalUpdateObj.endExposures
		? [...renewalUpdateObj.endExposures]
		: [];

	const referenceTimestamp = getReferenceTimestampBasedOnState(renewalResponse);

	const renewalCharacteristic = policy.characteristics
		.filter((c) =>
			filterForPolicySnapshot(
				c.createdTimestamp,
				c.replacedTimestamp,
				referenceTimestamp,
			),
		)
		.find((char) => {
			return (
				parseInt(char.startTimestamp, 10) <=
					parseInt(renewalStartTimestamp, 10) &&
				parseInt(char.endTimestamp, 10) >= parseInt(renewalStartTimestamp, 10)
			);
		});

	const hasPaymentPlanPluginEnabled = getHasPaymentPluginEnabled(productConfig);
	const paymentPlans = productConfig.policyConfiguration.paymentSchedules;
	const newPaymentPlanDisplayName = newPaymentScheduleName
		? paymentPlans.find((p) => p.name === newPaymentScheduleName).displayName
		: null;

	const hasUnsavedChanges = renewalHasUnsavedChanges(
		renewalResponse,
		renewalUpdateObj,
		isDirty,
		dirtyFields,
		productConfig,
		policy,
	);
	const renderUnsavedChangesWarning = () => {
		return (
			<Alert severity='warning'>
				{RENEWAL_REVIEW_CHANGES_UNSAVED_CHANGES_MSG()}
			</Alert>
		);
	};

	return (
		<StyledSectionContainer>
			<StyledTitleContainer>
				<StyledTitle>Review Changes</StyledTitle>
				{hasUnsavedChanges && renderUnsavedChangesWarning()}
			</StyledTitleContainer>
			<hr />
			<StyledRow>
				<FormControl style={{ marginRight: '10px' }}>
					<FieldLabel required={false} title={`Renewal End Date`} />
					<TextField
						className={classes.disabledInput}
						disabled
						id='renewalEnd'
						value={formatDateStringShort(renewalEndTimestamp) || ''}
					/>
				</FormControl>
				{newPaymentScheduleName && hasPaymentPlanPluginEnabled && (
					<FormControl style={{ marginRight: '10px' }}>
						<FieldLabel required={false} title={'New Payment Schedule'} />
						<TextField
							className={classes.disabledInput}
							disabled
							id='newPaymentSchedule'
							value={newPaymentPlanDisplayName || 'N/A'}
						/>
					</FormControl>
				)}
			</StyledRow>
			<PolicyChanges
				policyCharacteristic={{
					...renewalCharacteristic,
					fieldGroupsByLocator: getAllCharacteristicsFieldGroupLocators(policy),
					fieldValues: getAllCharacteristicsFieldValues(policy),
				}}
				changedFieldValues={changedFieldValues}
				addedFieldGroups={addedFieldGroups}
				removedFieldGroups={removedFieldGroups}
				updatedFieldGroups={updatedFieldGroups}
				fieldConfigurations={fieldConfigurations}
			/>
			<ExposureChanges
				policyCharacteristic={renewalCharacteristic}
				exposures={policy.exposures}
				characteristicTimestamp={renewalUpdateObj.renewalStartTimestamp.toString()}
				referenceTimestamp={referenceTimestamp}
				addedExposures={addedExposures}
				updatedExposures={updatedExposures}
				endedExposures={endedExposures}
				exposureConfigurations={exposureConfigurations}
				currency={policy?.currency}
			/>
		</StyledSectionContainer>
	);
};

export default withRenewalContext<Props>(ReviewChanges);
